import { BUYABLE_LISTING_CATEGORIES, isBuyableListingCategory } from "./buyableCategories";
import { normalizeStoredCondition, resolveCategoryKey } from "./productCondition";
import { categoryLeafValues } from "./listingCounts";
import { FOOD_HIERARCHY } from "./categoryHierarchy";

/**
 * Categories that represent physical-stock-based products inside a virtual
 * store, where a numeric "available quantity" is a meaningful concept and the
 * checkout flow decrements it on each sale.
 *
 * Intentionally EXCLUDES every category where logical stock makes no sense:
 *   - vasita (cars), emlak (real estate)        → unique single items
 *   - hizmetler, ozel-ders                      → services / lessons
 *   - is-ilanlari, yardimci-arayanlar           → job postings (not sellable)
 *   - is-makineleri (heavy machinery)           → unique high-value units
 *   - hayvanlar-alemi (animals)                 → individual living animals
 *
 * Anything outside this list either doesn't have a sellable inventory at all
 * (services, jobs) or is a single-unit listing.
 *
 * This list is shared between client (CreateListing form gating) and server
 * (POST /api/reseller/own-products validation) so the two layers cannot
 * disagree about whether a category needs a quantity. Adding a category here
 * automatically opts it into BOTH the visible quantity input on the form AND
 * the server-side guard that rejects publishing without a positive quantity.
 */
export const STOCK_BASED_OWN_PRODUCT_CATEGORIES = [
  "alisveris",
  "yedek-parca",
  "riyada",
  "atfal",
  "tibbi",
  "aghziya",
] as const;

/**
 * Categories a store (virtual or physical) may publish own products in.
 *
 * Policy: store own products are cart products ONLY — the same buyable
 * categories merchants publish wholesale products in
 * (see shared/buyableCategories.ts), plus "aghziya" (food), which is
 * stock-based cart commerce that simply has no wholesale channel.
 * Large classifieds (cars, real estate, services, jobs, animals, heavy
 * machinery) are contact-only ads and must never be sold as store own
 * products — those are published from a customer account instead.
 */
export const ALLOWED_OWN_PRODUCT_CATEGORIES = [
  ...BUYABLE_LISTING_CATEGORIES,
  "aghziya",
] as const;

export function isAllowedOwnProductCategory(
  category: string | null | undefined,
): boolean {
  if (!category || typeof category !== "string") return false;
  return (ALLOWED_OWN_PRODUCT_CATEGORIES as readonly string[]).includes(
    category,
  );
}

export type StockBasedOwnProductCategory =
  (typeof STOCK_BASED_OWN_PRODUCT_CATEGORIES)[number];

/**
 * True if a category represents physical, restock-able inventory and the
 * own-product flow MUST collect a quantity > 0 before publishing.
 *
 * Returns false for unknown / null / empty strings — callers should treat
 * those as "no quantity required" so we never block creation on a category
 * that simply hasn't been opted into the stock model.
 */
export function requiresStockQuantity(
  category: string | null | undefined,
): boolean {
  if (!category || typeof category !== "string") return false;
  return (STOCK_BASED_OWN_PRODUCT_CATEGORIES as readonly string[]).includes(
    category,
  );
}

/**
 * Store SERVICES (owner decision, 20 Oct 2026): a retail store may publish a
 * service as an own product in «الخدمات» (`hizmetler`) under one of its
 * leaves (design, printing, maintenance…). A service is contact-only: no
 * cart, no purchase order, no stock, no «جديد/مستعمل», no delivery, no money
 * movement — the customer contacts the store. Kept OUT of
 * ALLOWED_OWN_PRODUCT_CATEGORIES (cart products) and out of every shared
 * merchant list: only a retail-store role may add one (server
 * `getAccountRole(...) === "store"`, client `isStoreOwnerRole`).
 */
export const STORE_SERVICE_CATEGORY = "hizmetler" as const;

/**
 * The stock value a service row carries. The column is NOT NULL (default 1);
 * no service flow reads it (orders are refused), and the current clients
 * hide it. 1 rather than 0 so installed 1.0.5 store pages, which always print
 * an own-product stock badge, never show a service as «نفذت الكمية».
 */
export const STORE_SERVICE_STORED_QUANTITY = 1;

/** Is this stored category the services section (key or its Arabic name)? */
export function isStoreServiceCategory(category: string | null | undefined): boolean {
  if (!category || typeof category !== "string" || !category.trim()) return false;
  return resolveCategoryKey(category) === STORE_SERVICE_CATEGORY;
}

/** A store service must sit under a real leaf of the services tree. */
export function isStoreServiceSubcategory(subcategory: string | null | undefined): boolean {
  if (!subcategory || typeof subcategory !== "string") return false;
  return categoryLeafValues(STORE_SERVICE_CATEGORY).includes(subcategory.trim());
}

/**
 * Store FOOD (issue #176, owner decision 24 Oct 2026): a RETAIL store sells
 * its own physical food products through the existing cart — same orders,
 * stock, delivery and accounting as its other own products (no commission,
 * cash handed to the store). This phase covers fixed price and quantity items
 * (honey, rice, oils, canned and packaged food); chilled and frozen food
 * (meat, dairy) included, on the existing delivery (owner correction,
 * 24 Oct 2026). NOT in this phase (later, separate requirements): variable
 * weight, substitutes, and a new cooling system.
 *
 * Kept apart from every other channel on purpose: BUYABLE_LISTING_CATEGORIES
 * (regular-listing cart and wholesaler publishing) and the merchant lists are
 * NOT widened, so a customer's food ad stays contact-only and a wholesaler
 * gains no food channel. Only a retail-store role publishes food own products
 * (server `getAccountRole(...) === "store"`, client `isStoreOwnerRole`).
 */
export const STORE_FOOD_CATEGORY = "aghziya" as const;

const foodGroupLeaves = (groupId: string): readonly string[] =>
  FOOD_HIERARCHY.children?.find((group) => group.id === groupId)?.subcategories ?? [];

/**
 * «مستلزمات المطاعم والمقاهي»: filed under food but not eaten (cups,
 * packaging, napkins, equipment) — «جديد» or «مستعمل», like other goods.
 */
export const FOOD_NON_CONSUMABLE_SUBCATEGORIES: readonly string[] = foodGroupLeaves("restaurant-supplies");

const trimmed = (value: string | null | undefined): string =>
  typeof value === "string" ? value.trim() : "";

/** Is this stored category the food section (key or its Arabic name)? */
export function isStoreFoodCategory(category: string | null | undefined): boolean {
  const value = trimmed(category);
  return !!value && resolveCategoryKey(value) === STORE_FOOD_CATEGORY;
}

/** A store food product sits under a real leaf of the food tree. */
export function isStoreFoodSubcategory(subcategory: string | null | undefined): boolean {
  const value = trimmed(subcategory);
  return !!value && categoryLeafValues(STORE_FOOD_CATEGORY).includes(value);
}

/**
 * Food for human consumption: never sold as «مستعمل» by a store. Every food
 * product counts, unless it is filed under a restaurant-supplies leaf — a
 * food product with no (or an unknown) leaf is never assumed to be supplies.
 */
export function isHumanFoodProduct(
  category: string | null | undefined,
  subcategory: string | null | undefined,
): boolean {
  return isStoreFoodCategory(category) && !FOOD_NON_CONSUMABLE_SUBCATEGORIES.includes(trimmed(subcategory));
}

export type OwnProductCartBlock =
  | "SERVICE_NOT_ORDERABLE"
  | "CATEGORY_NOT_CART_ELIGIBLE"
  | "FOOD_SUBCATEGORY_REQUIRED"
  | "FOOD_MUST_BE_NEW"
  | "CONDITION_REQUIRED";

/**
 * Why a store OWN product cannot be bought through the cart, or null when it
 * can. ONE rule for every surface: the server's order route and every client
 * cart button (store page, product page, result cards, saved cart lines).
 *  - goods in the cart categories (BUYABLE_LISTING_CATEGORIES): as before;
 *  - food («الأغذية», stored key only, chilled and frozen included): a real
 *    food leaf and a saved condition — «جديد» for food that is eaten. Older food products
 *    keep their data and stay contact-only until their owner completes them;
 *  - services and anything else: contact only.
 */
export function ownProductCartBlock(product: {
  category?: string | null;
  subcategory?: string | null;
  condition?: string | null;
}): OwnProductCartBlock | null {
  const category = trimmed(product.category);
  if (isBuyableListingCategory(category)) return null;
  if (isStoreServiceCategory(category)) return "SERVICE_NOT_ORDERABLE";
  if (category !== STORE_FOOD_CATEGORY) return "CATEGORY_NOT_CART_ELIGIBLE";
  if (!isStoreFoodSubcategory(product.subcategory)) return "FOOD_SUBCATEGORY_REQUIRED";
  const condition = normalizeStoredCondition(product.condition);
  if (isHumanFoodProduct(category, product.subcategory)) return condition === "جديد" ? null : "FOOD_MUST_BE_NEW";
  return condition ? null : "CONDITION_REQUIRED";
}

export function isOwnProductCartEligible(product: {
  category?: string | null;
  subcategory?: string | null;
  condition?: string | null;
}): boolean {
  return ownProductCartBlock(product) === null;
}

export const STORE_FOOD_MUST_BE_NEW_MESSAGE =
  "الأغذية المخصصة للاستهلاك البشري تُعرض في منتجات المتجر بحالة «جديد» فقط، ولا تُباع على أنها «مستعملة».";
export const STORE_FOOD_SUBCATEGORY_REQUIRED_MESSAGE =
  "اختر نوع المنتج الغذائي من فروع قسم «الأغذية» (مثل عسل أو أرز أو زيت زيتون أو معلبات).";
export const STORE_FOOD_STORE_ONLY_MESSAGE =
  "منتجات قسم «الأغذية» متاحة لأصحاب متاجر التجزئة فقط.";
export const OWN_PRODUCT_NOT_ORDERABLE_MESSAGE =
  "هذا المنتج لا يُطلب عبر السلة. تواصل مع المتجر مباشرة لطلبه.";
